import { useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ListShell } from "@/components/shared/ListShell";
import {
  useDeleteFeature,
  useFeatures,
  type FeatureRow,
} from "@/hooks/useFeatures";
import { FeatureEditDialog } from "./FeatureEditDialog";

export const FeaturesCatalogSection = () => {
  const { data, isLoading, error, refetch } = useFeatures();
  const deleteFeature = useDeleteFeature();
  const features = data?.data ?? [];
  const grouped = useMemo(() => groupByCategory(features), [features]);

  const [editing, setEditing] = useState<FeatureRow | null>(null);
  const [open, setOpen] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (feature: FeatureRow) => {
    setEditing(feature);
    setOpen(true);
  };

  const onDelete = (feature: FeatureRow) => {
    if (
      !window.confirm(
        `Delete "${feature.name}"? Plans that reference it will lose it.`,
      )
    )
      return;
    deleteFeature.mutate(feature.id, {
      onSuccess: () => toast.success("Feature deleted"),
      onError: (e) => toast.error(e.message),
    });
  };

  return (
    <section className="rounded-card border border-border bg-card">
      <div className="p-5 pb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Feature catalog</h2>
          <p className="text-sm text-muted-foreground">
            The master list of features. Each plan toggles which ones are
            included via the plan editor.
          </p>
        </div>
        <Button onClick={openCreate} className="shrink-0">
          <Plus className="w-4 h-4 mr-1.5" /> Add feature
        </Button>
      </div>

      <div className="px-5 pb-5">
        <ListShell
          isLoading={isLoading}
          error={error}
          isEmpty={features.length === 0}
          onRetry={refetch}
          emptyTitle="No features yet"
          emptyDescription="Add a feature to make it available for plan inclusion."
        >
          <div className="space-y-5">
            {grouped.map(({ category, items }) => (
              <div key={category}>
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                  {category}
                </div>
                <ul className="divide-y divide-border rounded-btn border border-border">
                  {items.map((f) => (
                    <li
                      key={f.id}
                      className="flex items-center justify-between gap-3 px-3 py-2"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">
                            {f.name}
                          </span>
                          {!f.isActive && (
                            <span className="text-[10px] uppercase tracking-wide bg-muted text-muted-foreground px-1.5 py-0.5 rounded">
                              inactive
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {f.code}
                          {f.description ? ` · ${f.description}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEdit(f)}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => onDelete(f)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </ListShell>
      </div>

      <FeatureEditDialog open={open} onOpenChange={setOpen} feature={editing} />
    </section>
  );
};

const groupByCategory = (features: FeatureRow[]) => {
  const map = new Map<string, FeatureRow[]>();
  for (const f of features) {
    if (!map.has(f.category)) map.set(f.category, []);
    map.get(f.category)!.push(f);
  }
  return Array.from(map.entries())
    .map(([category, items]) => ({
      category,
      items: items.sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
      ),
    }))
    .sort((a, b) => a.category.localeCompare(b.category));
};

export default FeaturesCatalogSection;
